<template>
  <div class="tw-rounded-sm tw-bg-white tw-h-full">
    <div class="tw-flex tw-justify-between tw-items-center tw-p-6 tw-pb-2">
      <div class="tw-flex tw-items-center">
        <div class="tw-flex tw-items-center">
          <div
            v-tippy="{
              content: $t('email-security-solution'),
              placement: 'top',
            }"
            class="tw-mr-2 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-place-self-center tw-rounded-full tw-bg-white tw-p-0.5"
            :data-test-id="`${tier}-email-security-most-targeted-chart-icon`"
          >
            <component :is="SERVICE_ICON.PHISH_SERVICE" />
          </div>
        </div>
        <div class="tw-text-lg tw-font-semibold">
          {{ $t("most-targeted-users-title") }}
        </div>
      </div>
      <div
        v-tippy="{
          content: $t('most-targeted-users-tooltip'),
          placement: 'top',
        }"
        class="tw-text-primary-grey-200"
        :data-test-id="`${tier}-email-security-most-targeted-chart-info-icon`"
      >
        <cb-icon-information :width="18" />
      </div>
    </div>
    <t-skeleton v-if="isLoading" />
    <targeted-users-table :users="users" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useGlobalOrganisationIDBasedOnRoute } from "@/composables/useGlobalOrganisationIDBasedOnRoute";
import TSkeleton from "@/components/TSkeleton";
import { useIsMspRoute } from "@/composables/useIsMspRoute";
import { CUSTOMER, MSP } from "@/constants";
import TargetedUsersTable from "./TargetedUsersTable.vue";
import { SERVICE_ICON } from "@/utils/services";
import { useTargetedUsersStore } from "./useTargetedUsers.store";

const props = defineProps({
  startDate: {
    type: String,
    required: true,
  },
});

const { isMspRoute } = useIsMspRoute();

const tier = isMspRoute.value ? "msp" : "customer";
const { globalOrganisationId } = useGlobalOrganisationIDBasedOnRoute();
const isLoading = ref(true);

const {
  targetedUsers,
  getTargetedUsers: fetchTargetedUsersFromApi,
  resetTargetedUsers,
} = useTargetedUsersStore();
const users = computed(() => {
  if (targetedUsers.value.length === 0) {
    return [
      {
        email: "---",
        count: "#",
      },
    ];
  }
  if (targetedUsers.value.length > 6) {
    return targetedUsers.value.slice(0, 6);
  }

  return targetedUsers.value;
});

resetTargetedUsers();

const getTargetedUsers = async () => {
  if (!globalOrganisationId.value) {
    return;
  }
  try {
    isLoading.value = true;

    await fetchTargetedUsersFromApi({
      globalOrganisationID: globalOrganisationId.value,
      params: {
        start_date: props.startDate,
        organisationType: isMspRoute.value ? MSP : CUSTOMER,
      },
    });
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.startDate,
  () => getTargetedUsers(),
  { immediate: true },
);
</script>
